import { useEffect, useRef } from "react";
import { TAROT_PIN_VH } from "../../zodiacConfig";

const TAROT_CARDS = [
  {
    key: "past",
    label: "PAST",
    symbol: "moon",
    reading: "The moon marks what shaped you — a lesson learned, a door quietly closed. You've carried it further than you think.",
  },
  {
    key: "present",
    label: "PRESENT",
    symbol: "sun",
    reading: "The sun lights where you stand right now — steadier and more awake to your own power than you've given yourself credit for.",
  },
  {
    key: "future",
    label: "FUTURE",
    symbol: "star",
    reading: "The star points toward what's forming — a door opening right on time, the moment you stop waiting for permission.",
  },
];

const SYMBOLS = {
  moon: (
    <path d="M30 10a18 18 0 1 0 0 28 14 14 0 1 1 0-28Z" fill="currentColor" />
  ),
  sun: (
    <g stroke="currentColor" strokeWidth="2" fill="none">
      <circle cx="24" cy="24" r="9" fill="currentColor" stroke="none" />
      {[...Array(8)].map((_, i) => {
        const angle = (i / 8) * Math.PI * 2;
        const x1 = 24 + Math.cos(angle) * 14;
        const y1 = 24 + Math.sin(angle) * 14;
        const x2 = 24 + Math.cos(angle) * 20;
        const y2 = 24 + Math.sin(angle) * 20;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} strokeLinecap="round" />;
      })}
    </g>
  ),
  star: (
    <path
      d="M24 4 L29 18 L44 18 L32 27 L36 42 L24 33 L12 42 L16 27 L4 18 L19 18 Z"
      fill="currentColor"
    />
  ),
};

const CardBack = () => (
  <svg viewBox="0 0 200 300" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
    <rect x="0" y="0" width="200" height="300" fill="#1c1030" />
    <rect x="8" y="8" width="184" height="284" rx="14" fill="none" stroke="#E8C27A" strokeWidth="1.5" opacity="0.85" />
    <rect x="18" y="18" width="164" height="264" rx="8" fill="none" stroke="#E8C27A" strokeWidth="0.6" opacity="0.45" />
    <circle cx="100" cy="150" r="38" fill="none" stroke="#E8C27A" strokeWidth="1" opacity="0.55" />
    <path d="M92 132a20 20 0 1 0 0 36 16 16 0 1 1 0-36Z" fill="#E8C27A" opacity="0.75" />
    {[
      [100, 60], [100, 240], [40, 150], [160, 150],
      [55, 75], [145, 75], [55, 225], [145, 225],
    ].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="2.2" fill="#E8C27A" opacity="0.6" />
    ))}
    <path d="M24 24 L40 24 M24 24 L24 40" stroke="#E8C27A" strokeWidth="1" opacity="0.6" fill="none" />
    <path d="M176 24 L160 24 M176 24 L176 40" stroke="#E8C27A" strokeWidth="1" opacity="0.6" fill="none" />
    <path d="M24 276 L40 276 M24 276 L24 260" stroke="#E8C27A" strokeWidth="1" opacity="0.6" fill="none" />
    <path d="M176 276 L160 276 M176 276 L176 260" stroke="#E8C27A" strokeWidth="1" opacity="0.6" fill="none" />
  </svg>
);

const CardFront = ({ card }) => (
  <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-[#f6ecd2] to-[#e7d6a8] border border-[#E8C27A] flex flex-col items-center justify-center p-6 text-center">
    <svg width="44" height="44" viewBox="0 0 48 48" className="mb-5 text-[#6b4a1d]">
      {SYMBOLS[card.symbol]}
    </svg>
    <h3 className="font-display text-xl tracking-[0.15em] text-[#4a3312] mb-4">{card.label}</h3>
    <p className="text-sm text-[#5c4624] font-light leading-relaxed">{card.reading}</p>
  </div>
);

export const Tarot = () => {
  const sectionRef = useRef(null);
  const sceneRefs = useRef([]);
  const flipRefs = useRef([]);

  useEffect(() => {
    if (!window.gsap || !window.ScrollTrigger || !sectionRef.current) return;
    const gsapLib = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    gsapLib.registerPlugin(ScrollTrigger);

    const pinDistance = window.innerHeight * TAROT_PIN_VH;
    const st = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: "top top",
      end: `+=${pinDistance}`,
      pin: true,
      scrub: true,
      onUpdate: (self) => {
        flipRefs.current.forEach((el, i) => {
          if (!el) return;
          const start = i / 3;
          const end = (i + 1) / 3;
          const bandProgress = Math.min(Math.max((self.progress - start) / (end - start), 0), 1);
          gsapLib.set(el, { rotationY: bandProgress * 180 });
        });
      },
    });

    return () => st.kill();
  }, []);

  const handleMove = (i) => (e) => {
    const el = sceneRefs.current[i];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `rotateX(${-py * 14}deg) rotateY(${px * 14}deg)`;
  };

  const handleLeave = (i) => () => {
    const el = sceneRefs.current[i];
    if (el) el.style.transform = "rotateX(0deg) rotateY(0deg)";
  };

  return (
    <section id="tarot" ref={sectionRef} className="relative min-h-screen w-full flex items-center overflow-hidden">
      <div className="cosmic-section w-full max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <p className="cosmic-eyebrow text-sm mb-5">A Three-Card Reading</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">Past. Present. Future.</h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-10 max-w-4xl mx-auto">
          {TAROT_CARDS.map((card, i) => (
            <div
              key={card.key}
              data-testid={`tarot-card-scene-${card.key}`}
              className="tarot-flip-scene aspect-[2/3] w-full"
              ref={(el) => (sceneRefs.current[i] = el)}
              onMouseMove={handleMove(i)}
              onMouseLeave={handleLeave(i)}
            >
              <div className="tarot-flip-card" ref={(el) => (flipRefs.current[i] = el)}>
                <div data-testid={`tarot-card-back-${card.key}`} className="tarot-flip-face tarot-flip-back">
                  <CardBack />
                </div>
                <div data-testid={`tarot-card-front-${card.key}`} className="tarot-flip-face tarot-flip-front">
                  <CardFront card={card} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
